import firebase from "firebase";
import { useTranslation } from "next-i18next";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import Heading from "@/features/common/components/tailwind/Heading";
import { useFeatureEnabled } from "@/features/common/hooks/useFeatureEnabled";
import { JoinSubscriptionPaymentButton } from "@/features/subscription/components/joinSubscriptionDialog/JoinSubscriptionPaymentButton";
import { JoinSubscriptionPaymentViewPlaceholder } from "@/features/subscription/components/joinSubscriptionDialog/JoinSubscriptionPaymentViewPlaceholder";
import { PaymentMethodTypeEnum } from "@/features/subscription/components/joinSubscriptionDialog/type";
import { TipsByStripeView } from "@/features/tips/components/tipsPaymentDialog/TipsByStripeView";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  useUserPaymentMethodsQuery,
  PaymentMethodStatusEnum,
  PaymentMethodProviderEnum,
  FeatureNameEnum,
} from "@/generated/generated-hooks";

export const TipsPaymentView = ({ onClose }) => {
  const { t } = useTranslation(["tips", "subscriptionPlan", "common"]);
  const { register, control } = useFormContext();
  const { isEnabled } = useFeatureEnabled();
  const isOfflinePaymentEnabled = isEnabled(FeatureNameEnum.OfflinePayment);
  const { ready, user } = useUserContext();
  const [payAs, setPayAs] = useState<PaymentMethodTypeEnum>(
    PaymentMethodTypeEnum.CreditCard,
  );
  const { data, loading, error } = useUserPaymentMethodsQuery({
    variables: {
      userId: user._id,
      paymentMethodProvider: PaymentMethodProviderEnum.Stripe2,
      paymentMethodStatus: [
        PaymentMethodStatusEnum.Default,
        PaymentMethodStatusEnum.Active,
      ],
    },
    skip: !ready || !user,
    ssr: false,
  });

  const showDefault = payAs === PaymentMethodTypeEnum.CreditCard;

  if (loading || !data) return <JoinSubscriptionPaymentViewPlaceholder />;

  const onClickCreditCard = () => {
    firebase
      .analytics()
      .logEvent(AnalyticsEventEnum.PaymentConfirmClickDefault, {
        feature: "tips",
      });

    setPayAs(PaymentMethodTypeEnum.CreditCard);
  };

  const onClickOffline = () => {
    firebase
      .analytics()
      .logEvent(AnalyticsEventEnum.PaymentConfirmClickOthers, {
        feature: "tips",
      });

    setPayAs(PaymentMethodTypeEnum.Offline);
  };

  return (
    <div className="w-full flex flex-col bg-gray-50 p-5 mb-8">
      <Heading.H5 gutterBottom={true} className="text-20">
        {t("common:paymentForm.title")}
      </Heading.H5>

      {/* options */}
      <div className="w-full flex flex-col space-y-2 md:space-y-0 md:flex-row md:space-x-2">
        <div className="flex-1">
          <JoinSubscriptionPaymentButton
            active={showDefault}
            onClick={onClickCreditCard}
            label={t("subscriptionPlan:creditCardPaymentMethod")}
          />
        </div>
        {isOfflinePaymentEnabled && (
          <div className="flex-1">
            <JoinSubscriptionPaymentButton
              active={!showDefault}
              onClick={onClickOffline}
              label={t("subscriptionPlan:offlinePaymentMethod")}
            />
          </div>
        )}
      </div>

      {/* payment form */}
      <TipsByStripeView />
    </div>
  );
};
